import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { jotaiStore } from 'init/jotai';
import { i18nKeys, translationStateAtom } from 'locales';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const { t } = jotaiStore.get(translationStateAtom);

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/pdf'];

const attachmentsSchema = z
  .any()
  .refine((file: File) => file.size <= MAX_FILE_SIZE, 'Max image size is 5MB.')
  .refine((file: File) => ACCEPTED_IMAGE_TYPES.includes(file.type), 'Invalid image type.')
  .nullable();

const emailListSchema = (required = false) =>
  z
    .array(z.string())
    .refine(arr => !required || arr.length > 0, {
      error: t(i18nKeys.EXTERNAL_MAIL.RECIPIENT_REQUIRED),
    })
    .refine(arr => arr.every(val => z.email().safeParse(val).success), {
      error: t(i18nKeys.EXTERNAL_MAIL.INVALID_EMAIL),
    });

export const createSendEmailSchema = z.object({
  body: z.string().refine(val => val.replace(/<[^>]*>/g, '').trim().length > 0, {
    error: t(i18nKeys.EXTERNAL_MAIL.BODY_REQUIRED),
  }),
  threadId: z.string().optional(),
  draftId: z.string().optional(),
  subject: z.string().min(1, { error: t(i18nKeys.EXTERNAL_MAIL.SUBJECT_REQUIRED) }),
  toMail: emailListSchema(true),
  ccMail: emailListSchema(),
  bccMail: emailListSchema(),
  attachments: z.array(attachmentsSchema).optional(),
});

export type SendEmail = z.infer<typeof createSendEmailSchema>;

function createFormData(data: SendEmail) {
  const formData = new FormData();

  if (data.threadId) formData.append('threadId', data.threadId);
  if (data.draftId) formData.append('draft_id', data.draftId);
  formData.append('body', data.body);
  formData.append('subject', data.subject);

  for (const email of data.toMail) formData.append('to_mail[]', email);
  for (const email of data.ccMail) formData.append('cc_mail[]', email);
  for (const email of data.bccMail) formData.append('bcc_mail[]', email);
  for (const file of data.attachments ?? []) formData.append('attachments[]', file);

  return formData;
}

export async function sendEmailFn(axiosInstance: Promise<AxiosInstance>, data: SendEmail) {
  const instance = await axiosInstance;
  const formData = createFormData(data);

  return instance.post('mails', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function useSendEmail() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, SendEmail>({
    mutationFn: data => sendEmailFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['mails'] });
      client.invalidateQueries({ queryKey: ['mail'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, SendEmail, 'sendEmail'>(
    'sendEmail',
    mutation,
  );
}
